import { DOCUMENT } from '@angular/common';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { HttpParams } from '@angular/common/http';
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    Renderer2,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { Client } from '../../../../../projects/kiene-core/src/lib/components/client/classes/client';
import { LocalStorageService } from '../../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { KieneBackendApiService } from '../../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import {
    getGruppeLinksForRow,
    getLetzteAktionenDisplayedChipClass,
    getLetzteAktionenDisplayedStatusLabel,
    canShowAusstallenErloesTageszunahmen,
    getLetzteAktionenStatusPhase,
    formatLetzteAktionPreisGesamt,
    formatLetzteAktionGewichtGesamt,
    parseWertAnzahl,
    getTierLetzteAktionDisplayLabel,
    isTierlisteClosed,
    canContinueDirectScanEinstall,
    isDirectScanEinstallTierliste,
    LetzteAktionenApiResponse,
    LetzteAktionWertAnzahl,
    mapLetzteAktionApiRow,
    loadLetzteAktionenZeitraum,
    saveLetzteAktionenZeitraum,
    LETZTE_AKTIONEN_FILTER_SPEICHER_KEY,
    mergeLetzteAktionenFilterSpeicher,
    parseLetzteAktionenZeitraum,
    LetzteAktionenZeitraum,
    LETZTE_AKTIONEN_ZEITRAUM_DEFAULT,
    LETZTE_AKTIONEN_ZEITRAUM_OPTIONS,
    getLetzteAktionAktuelleGruppeDisplay as getLetzteAktionAktuelleGruppeDisplayForRow,
    shouldShowAktuelleGruppeForRow as shouldShowAktuelleGruppeForLetzteAktionRow,
    shouldShowGewichtPreisMetricsForLetzteAktionRow,
    shouldShowAbschnittForLetzteAktionRow,
    getLetzteAktionAbschnittDisplay as getLetzteAktionAbschnittDisplayForRow,
    getLetzteAktionHerkunftZielDisplay,
    formatLetzteAktionBewegungsdatum,
    isLetzteAktionImportPrioritaet,
    isPendingImportLetzteAktionRow,
    TierLetzteAktionGruppeLink,
    TierLetzteAktionRow,
    TierLetzteAktionTyp,
} from './letzte-aktionen.models';
import {
    isLetzteAktionenGewichtColumn,
    isLetzteAktionenPreisColumn,
    LetzteAktionenColumnDef,
    LetzteAktionenColumnKey,
    loadLetzteAktionenColumns,
    saveLetzteAktionenColumns,
} from './letzte-aktionen-columns';
import {
    LetzteAktionenTierlisteMenuComponent,
    LetzteAktionenTierlisteMenuMode,
} from './letzte-aktionen-tierliste-menu.component';
import { LetzteAktionenTierlisteMenuCacheService } from './letzte-aktionen-tierliste-menu-cache.service';
import { DashboardEinstallenStepperDialogComponent } from '../dashboard-einstallen-stepper-dialog/dashboard-einstallen-stepper-dialog.component';
import {
    DashboardEinstallenStepperDialogData,
    dashboardEinstallenStepperDialogLayout,
} from '../dashboard-einstallen-stepper-dialog/dashboard-einstallen-stepper-dialog.data';
import { LetzteAktionenDetailDialogComponent } from './letzte-aktionen-detail-dialog/letzte-aktionen-detail-dialog.component';
import { PaesseAbgleichenComponent } from '../../shared/paesse-abgleichen/paesse-abgleichen.component';

const PERMISSION_GEWICHTE = 9161;
const PERMISSION_EINSTALLEN_PRIMARY = 1912;
const PERMISSION_EINSTALLEN_SECONDARY = 9122;
const PERMISSION_PREISE = 9162;

interface LetzteAktionenDetailCellContent {
    label: string;
    value: string;
}
type LetzteAktionenSortableKey =
    | 'aktion_id'
    | 'aktion'
    | 'status'
    | 'betrieb'
    | 'anzahl_tiere'
    | 'aktuelle_gruppe'
    | 'herkunft'
    | 'datum';

@Component({
    selector: 'kiene-letzte-aktionen-panel',
    templateUrl: './letzte-aktionen-panel.component.html',
    styleUrls: ['./letzte-aktionen-panel.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
            transition('void => expanded', [
                style({ height: '0px', minHeight: '0' }),
                animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ height: '*' })),
            ]),
        ]),
    ],
})
export class LetzteAktionenPanelComponent implements OnInit, AfterViewInit, OnDestroy {
    readonly defaultPageSize = 100;
    readonly pageSizeOptions = [5, 10, 20, 50, 100];
    readonly einstallenAktion = TierLetzteAktionTyp.einstallen;
    readonly ausstallenAktion = TierLetzteAktionTyp.ausstallen;

    loading = false;
    error: string | null = null;
    metricsLoading = false;
    highlightedTierlisteId: number | null = null;
    private highlightedTierlisteMatchIds = new Set<number>();

    actions: TierLetzteAktionRow[] = [];
    totalCount = 0;
    pageIndex = 0;
    pageSize = this.defaultPageSize;
    columns: LetzteAktionenColumnDef[] = loadLetzteAktionenColumns();
    activeSortKey: LetzteAktionenSortableKey = 'datum';
    activeSortDirection: 'asc' | 'desc' = 'desc';
    readonly zeitraumOptions = LETZTE_AKTIONEN_ZEITRAUM_OPTIONS;
    readonly defaultZeitraum = LETZTE_AKTIONEN_ZEITRAUM_DEFAULT;
    /** Like Abgabebelege `openFirstRows`: the newest rows start expanded. */
    private readonly autoExpandRowCount = 5;
    activeZeitraum: LetzteAktionenZeitraum = loadLetzteAktionenZeitraum();

    /** Parent dashboard panels (e.g. Bereit zum Importieren) can listen and reload. */
    @Output() refreshRequested = new EventEmitter<void>();

    @ViewChildren(LetzteAktionenTierlisteMenuComponent)
    private readonly rowMenus?: QueryList<LetzteAktionenTierlisteMenuComponent>;

    @ViewChild('actionsTableViewport')
    private actionsTableViewport?: ElementRef<HTMLElement>;

    @ViewChild('letzteAktionenTable')
    private actionsTableNative?: ElementRef<HTMLElement>;

    @ViewChild('bottomScrollbar')
    private bottomScrollbar?: ElementRef<HTMLElement>;

    @ViewChild('paesseAbgleichen')
    private paesseAbgleichen?: PaesseAbgleichenComponent;

    /** Matches kiene-table (/gruppen/aktuelle): fixed bottom bar + hidden native scrollbar. */
    horizontalScrollWidth = 0;
    showHorizontalScrollbar = false;
    private syncFromTable = false;
    private syncFromBottom = false;
    private bottomScrollbarMountedToBody = false;
    private horizontalScrollResizeObserver?: ResizeObserver;

    private watchClientSubscription: Subscription;
    private loadActionsSubscription?: Subscription;
    private zeitraumSpeicherSubscription?: Subscription;
    private currentClient: Client;
    private pendingAutoScrollTierlisteId: number | null = null;
    private expandedRows = new WeakSet<TierLetzteAktionRow>();
    private readonly detailColumnKeys = new Set<LetzteAktionenColumnKey>([
        'abschnitt',
        'einkaufspreis',
        'verkaufspreis',
        'erloesdifferenz',
        'tageszunahmen',
    ]);

    constructor(
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly api: KieneBackendApiService,
        private readonly localStorageService: LocalStorageService,
        private readonly tierlisteMenuCache: LetzteAktionenTierlisteMenuCacheService,
        private readonly dialog: MatDialog,
        private readonly renderer: Renderer2,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {}

    ngOnInit(): void {
        this.currentClient = this.localStorageService.getCurrentClient();
        this.hydrateZeitraumFromSpeicher();
        this.resetPaginationAndLoad();

        this.watchClientSubscription = this.localStorageService.watchCurrentClient().subscribe(client => {
            this.currentClient = client;
            this.resetPaginationAndLoad();
        });
    }

    ngAfterViewInit(): void {
        this.mountBottomScrollbarToBody();
        this.initHorizontalScrollbarObserver();
        this.scheduleHorizontalScrollbarSync();
    }

    ngOnDestroy(): void {
        this.horizontalScrollResizeObserver?.disconnect();
        this.horizontalScrollResizeObserver = undefined;
        this.unmountBottomScrollbarFromBody();
        this.loadActionsSubscription?.unsubscribe();
        this.zeitraumSpeicherSubscription?.unsubscribe();
        this.watchClientSubscription?.unsubscribe();
    }

    get showPaginator(): boolean {
        return !this.error && this.totalCount > 0;
    }

    getAktionLabel(row: TierLetzteAktionRow): string {
        return getTierLetzteAktionDisplayLabel(row);
    }

    isAutomatischeSchlachtung(row: TierLetzteAktionRow): boolean {
        return this.isAutomatischerKommentar(row);
    }

    /** Auto-generierte HIT-/Schlachtungs-Kommentare (optische Hervorhebung). */
    isAutomatischerKommentar(row: TierLetzteAktionRow): boolean {
        const k = (row.kommentar || '').toLowerCase();
        if (!k.includes('automatisch')) {
            return false;
        }
        return k.includes('schlachtung') || k.includes('hit sync') || k.includes('hit abmeldung');
    }

    togglePaesseAbgleichen(): void {
        this.paesseAbgleichen?.toggleSearchField();
    }

    onPaesseAbgleichenTierlisteFound(tierlisteId: number | null): void {
        const nextId = Number(tierlisteId || 0) || null;
        const previousId = Number(this.highlightedTierlisteId || 0) || null;
        this.highlightedTierlisteId = nextId;
        if (!nextId) {
            this.pendingAutoScrollTierlisteId = null;
            this.highlightedTierlisteMatchIds.clear();
            return;
        }
        if (nextId !== previousId) {
            this.pendingAutoScrollTierlisteId = nextId;
        }
        this.highlightedTierlisteMatchIds = new Set([nextId]);
        this.navigateToTierlisteAction(nextId);
    }

    private navigateToTierlisteAction(tierlisteId: number): void {
        let params = new HttpParams()
            .set('find_tierliste_id', String(tierlisteId))
            .set('sort_key', this.activeSortKey)
            .set('sort_dir', this.activeSortDirection)
            .set('zeitraum', this.activeZeitraum);
        if (this.currentClient?.kunde_id) {
            params = params.set('kunde_id', this.currentClient.kunde_id);
        }

        this.api.get(this.apiBaseUrl + 'gruppe/read_letzte_aktionen.php', params).subscribe({
            next: (response: { found?: boolean; offset?: number | null; match_ids?: Array<number | string> }) => {
                const matchIds = (response?.match_ids ?? [tierlisteId])
                    .map(id => Number(id))
                    .filter(id => id > 0);
                if (matchIds.length > 0) {
                    this.highlightedTierlisteMatchIds = new Set(matchIds);
                }

                if (!response?.found || response.offset == null || response.offset < 0) {
                    this.pendingAutoScrollTierlisteId = null;
                    return;
                }

                const targetPageIndex = Math.floor(response.offset / this.pageSize);
                if (targetPageIndex !== this.pageIndex) {
                    this.pageIndex = targetPageIndex;
                    this.loadCurrentPage();
                    return;
                }

                this.scheduleScrollToHighlightedTierliste();
            },
            error: () => {
                this.pendingAutoScrollTierlisteId = null;
            },
        });
    }

    get highlightedTierlisteHint(): string | null {
        const id = Number(this.highlightedTierlisteId || 0);
        if (id <= 0) {
            return null;
        }
        if (this.actions.some(row => this.isHighlightedTierlisteRow(row))) {
            return `Treffer: Tierliste #${id} gefunden.`;
        }
        if (this.pendingAutoScrollTierlisteId) {
            return `Tierliste #${id} wird gesucht…`;
        }
        return `Tierliste #${id} wurde nicht in den Letzten Aktionen gefunden (aktueller Zeitraum).`;
    }

    isHighlightedTierlisteRow(row: TierLetzteAktionRow): boolean {
        if (!this.highlightedTierlisteId) {
            return false;
        }
        return this.matchesHighlightedTierliste(row, this.highlightedTierlisteId);
    }

    getStatusChipClass(row: TierLetzteAktionRow): string {
        return getLetzteAktionenDisplayedChipClass(row);
    }

    getStatusLabel(row: TierLetzteAktionRow): string {
        return getLetzteAktionenDisplayedStatusLabel(row);
    }

    getStatusHinweis(row: TierLetzteAktionRow): string | null {
        return row.status_hinweis?.trim() || null;
    }

    getStatusTooltip(row: TierLetzteAktionRow): string | null {
        return this.getStatusHinweis(row);
    }

    getGruppeLinks(row: TierLetzteAktionRow): TierLetzteAktionGruppeLink[] {
        return getGruppeLinksForRow(row);
    }

    shouldShowAktuelleGruppeForRow(row: TierLetzteAktionRow): boolean {
        return shouldShowAktuelleGruppeForLetzteAktionRow(row);
    }

    getLetzteAktionAktuelleGruppeDisplay(row: TierLetzteAktionRow): string {
        return getLetzteAktionAktuelleGruppeDisplayForRow(row);
    }

    readonly getLetzteAktionHerkunftZielDisplay = getLetzteAktionHerkunftZielDisplay;
    readonly getBewegungsdatumLabel = formatLetzteAktionBewegungsdatum;

    /** Sync Herkunft/Händler to matching Einstallen rows without reloading the list. */
    private patchEinstallOriginFields(source: TierLetzteAktionRow): void {
        const tierlisteId = source.tierliste_id;
        if (!tierlisteId || tierlisteId <= 0) {
            return;
        }
        for (const actionRow of this.actions) {
            if (actionRow.tierliste_id === tierlisteId && actionRow.aktion === TierLetzteAktionTyp.einstallen) {
                actionRow.herkunft = source.herkunft;
                actionRow.handler = source.handler ?? null;
            }
        }
        this.tierlisteMenuCache.invalidate(tierlisteId);
    }

    getLetzteAktionAbschnittDisplay(row: TierLetzteAktionRow): string {
        return getLetzteAktionAbschnittDisplayForRow(row);
    }

    get canShowPreisColumns(): boolean {
        return (
            this.localStorageService.hasPermission(PERMISSION_PREISE) &&
            this.localStorageService.isCurrentClientModulAllowed('bio')
        );
    }

    get canShowGewichtColumns(): boolean {
        return (
            this.localStorageService.hasPermission(PERMISSION_GEWICHTE) &&
            this.localStorageService.isCurrentClientModulAllowed('bio')
        );
    }

    private rowDarfBio(row: { darf_bio?: unknown } | null | undefined): boolean {
        return this.localStorageService.isRecordModulAllowed(row, 'bio');
    }

    getSpaltenMenuColumns(): LetzteAktionenColumnDef[] {
        return this.columns.filter(col => {
            if (this.detailColumnKeys.has(col.key)) {
                return false;
            }
            if (isLetzteAktionenPreisColumn(col.key)) {
                return this.canShowPreisColumns;
            }
            if (isLetzteAktionenGewichtColumn(col.key)) {
                return this.canShowGewichtColumns;
            }
            return true;
        });
    }

    getVisibleColumns(): LetzteAktionenColumnDef[] {
        return this.getSpaltenMenuColumns().filter(col => col.visible);
    }

    isColumnSortable(key: LetzteAktionenColumnKey): key is LetzteAktionenSortableKey {
        return (
            key === 'aktion_id' ||
            key === 'aktion' ||
            key === 'status' ||
            key === 'betrieb' ||
            key === 'anzahl_tiere' ||
            key === 'aktuelle_gruppe' ||
            key === 'herkunft' ||
            key === 'datum'
        );
    }

    sortByColumn(key: LetzteAktionenColumnKey): void {
        if (!this.isColumnSortable(key)) {
            return;
        }
        if (this.activeSortKey === key) {
            this.activeSortDirection = this.activeSortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.activeSortKey = key;
            this.activeSortDirection = key === 'datum' ? 'desc' : 'asc';
        }
        this.expandedRows = new WeakSet<TierLetzteAktionRow>();
        this.pageIndex = 0;
        this.loadCurrentPage();
    }

    getColumnSortIcon(key: LetzteAktionenColumnKey): string {
        if (!this.isColumnSortable(key)) {
            return 'unfold_more';
        }
        if (this.activeSortKey !== key) {
            return 'unfold_more';
        }
        return this.activeSortDirection === 'asc' ? 'north' : 'south';
    }

    onActionsTableScroll(): void {
        if (this.syncFromBottom) {
            return;
        }
        const tableEl = this.actionsTableViewport?.nativeElement;
        const bottomEl = this.bottomScrollbar?.nativeElement;
        if (!tableEl || !bottomEl) {
            return;
        }
        this.syncFromTable = true;
        bottomEl.scrollLeft = tableEl.scrollLeft;
        this.syncFromTable = false;
    }

    onBottomScrollbarScroll(): void {
        if (this.syncFromTable) {
            return;
        }
        const tableEl = this.actionsTableViewport?.nativeElement;
        const bottomEl = this.bottomScrollbar?.nativeElement;
        if (!tableEl || !bottomEl) {
            return;
        }
        this.syncFromBottom = true;
        tableEl.scrollLeft = bottomEl.scrollLeft;
        this.syncFromBottom = false;
    }

    isMetricsLoadingRow(row: TierLetzteAktionRow): boolean {
        return this.metricsLoading && row.tierliste_id != null && row.tierliste_id > 0;
    }

    isRowExpanded(row: TierLetzteAktionRow): boolean {
        return this.expandedRows.has(row);
    }

    getDetailExpandState(row: TierLetzteAktionRow): 'expanded' | 'collapsed' {
        return this.expandedRows.has(row) ? 'expanded' : 'collapsed';
    }

    toggleRowExpanded(row: TierLetzteAktionRow, event?: Event): void {
        event?.stopPropagation();
        if (this.expandedRows.has(row)) {
            this.expandedRows.delete(row);
            return;
        }
        this.expandedRows.add(row);
    }

    /** Newest rows of the current page start open (Abgabebelege `openFirstRows`). */
    private expandInitialRows(rows: TierLetzteAktionRow[]): void {
        this.expandedRows = new WeakSet<TierLetzteAktionRow>();
        const count = Math.min(this.autoExpandRowCount, rows.length);
        for (let i = 0; i < count; i++) {
            this.expandedRows.add(rows[i]);
        }
    }

    openDetailDialog(row: TierLetzteAktionRow, event?: Event): void {
        event?.stopPropagation();
        this.dialog
            .open(LetzteAktionenDetailDialogComponent, {
                width: '680px',
                maxWidth: '96vw',
                maxHeight: '94vh',
                autoFocus: false,
                restoreFocus: false,
                data: {
                    row,
                    onRowChanged: options => {
                        if (options?.reload === false) {
                            this.patchEinstallOriginFields(row);
                            return;
                        }
                        if (row.tierliste_id) {
                            this.tierlisteMenuCache.invalidate(row.tierliste_id);
                        }
                        this.refreshActions();
                    },
                },
            })
            .afterClosed()
            .subscribe({
                next: saved => {
                    if (saved) {
                        if (row.tierliste_id) {
                            this.tierlisteMenuCache.invalidate(row.tierliste_id);
                        }
                        this.refreshActions();
                    }
                },
            });
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.syncHorizontalScrollbar();
    }

    toggleColumn(key: LetzteAktionenColumnKey): void {
        const col = this.columns.find(c => c.key === key);
        if (!col) {
            return;
        }
        col.visible = !col.visible;
        if (col.visible && col.key === 'verkaufspreis') {
            const erloes = this.columns.find(c => c.key === 'erloesdifferenz');
            if (erloes) {
                erloes.visible = true;
            }
            const tageszunahmen = this.columns.find(c => c.key === 'tageszunahmen');
            if (tageszunahmen && this.canShowGewichtColumns) {
                tageszunahmen.visible = true;
            }
        }
        saveLetzteAktionenColumns(this.columns);
        this.scheduleHorizontalScrollbarSync();
    }

    showAusstallPreisValue(row: TierLetzteAktionRow, col: LetzteAktionenColumnDef): boolean {
        if (!col.ausstallOnly) {
            return true;
        }
        return row.aktion === this.ausstallenAktion;
    }

    canShowDetailMetricRow(row: TierLetzteAktionRow): boolean {
        return this.canShowPreisColumns || this.canShowGewichtColumns || row.aktion === this.einstallenAktion;
    }

    getExpandedAbschnitt(row: TierLetzteAktionRow): string {
        return getLetzteAktionAbschnittDisplayForRow(row);
    }

    shouldShowGewichtPreisMetricsForRow(row: TierLetzteAktionRow): boolean {
        return shouldShowGewichtPreisMetricsForLetzteAktionRow(row);
    }

    getExpandedEinkaufspreis(row: TierLetzteAktionRow): string {
        if (!this.rowDarfBio(row)) {
            return './.';
        }
        if (!this.canShowPreisColumns || !this.shouldShowGewichtPreisMetricsForRow(row)) {
            return '—';
        }
        if (this.hasPreisWert(row.einkaufspreis_avg)) {
            return formatLetzteAktionPreisGesamt(row.einkaufspreis_avg);
        }
        return '—';
    }

    getExpandedVerkaufspreis(row: TierLetzteAktionRow): string {
        if (!this.rowDarfBio(row)) {
            return './.';
        }
        if (!this.canShowPreisColumns || !this.shouldShowGewichtPreisMetricsForRow(row)) {
            return '—';
        }
        if (this.hasPreisWert(row.verkaufspreis_avg)) {
            return formatLetzteAktionPreisGesamt(row.verkaufspreis_avg);
        }
        return '—';
    }

    getExpandedEinstallgewicht(row: TierLetzteAktionRow): string {
        if (!this.rowDarfBio(row)) {
            return './.';
        }
        if (!this.canShowGewichtColumns || !this.shouldShowGewichtPreisMetricsForRow(row)) {
            return '—';
        }
        if (this.hasPreisWert(row.einstallgewicht_avg)) {
            return formatLetzteAktionGewichtGesamt(row.einstallgewicht_avg);
        }
        return '—';
    }

    getExpandedAbgangsgewicht(row: TierLetzteAktionRow): string {
        if (!this.rowDarfBio(row)) {
            return './.';
        }
        if (row.aktion !== this.ausstallenAktion) {
            return '—';
        }
        if (!this.canShowGewichtColumns || !this.shouldShowGewichtPreisMetricsForRow(row)) {
            return '—';
        }
        if (this.hasPreisWert(row.abgangsgewicht_avg)) {
            return formatLetzteAktionGewichtGesamt(row.abgangsgewicht_avg);
        }
        return '—';
    }

    getExpandedErloesdifferenz(row: TierLetzteAktionRow): string {
        if (!this.rowDarfBio(row)) {
            return './.';
        }
        if (!canShowAusstallenErloesTageszunahmen(row)) {
            return '—';
        }
        if (!this.canShowPreisColumns || !this.shouldShowGewichtPreisMetricsForRow(row)) {
            return '—';
        }
        if (this.hasPreisWert(row.erloesdifferenz_avg)) {
            return `${Number(row.erloesdifferenz_avg!.wert).toLocaleString('de-DE', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })} €`;
        }
        return '—';
    }

    getExpandedTageszunahmen(row: TierLetzteAktionRow): string {
        if (!this.rowDarfBio(row)) {
            return './.';
        }
        if (!canShowAusstallenErloesTageszunahmen(row)) {
            return '—';
        }
        if (!this.canShowGewichtColumns || !this.shouldShowGewichtPreisMetricsForRow(row)) {
            return '—';
        }
        if (this.hasPreisWert(row.tageszunahmen_avg)) {
            return `${Number(row.tageszunahmen_avg!.wert).toLocaleString('de-DE', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
            })} kg`;
        }
        return '—';
    }

    getExpandedDetailCell(
        row: TierLetzteAktionRow,
        col: LetzteAktionenColumnDef,
    ): LetzteAktionenDetailCellContent | null {
        switch (col.key) {
            case 'aktion':
                if (!shouldShowAbschnittForLetzteAktionRow(row)) {
                    return null;
                }
                return { label: 'Abschnitt', value: this.getExpandedAbschnitt(row) };
            case 'status':
                if (!this.canShowGewichtColumns || !this.shouldShowGewichtPreisMetricsForRow(row)) {
                    return null;
                }
                return { label: 'Einstallgewicht', value: this.getExpandedEinstallgewicht(row) };
            case 'betrieb':
                if (
                    row.aktion !== this.ausstallenAktion ||
                    !this.canShowGewichtColumns ||
                    !this.shouldShowGewichtPreisMetricsForRow(row)
                ) {
                    return null;
                }
                return { label: 'Abgangsgewicht', value: this.getExpandedAbgangsgewicht(row) };
            case 'anzahl_tiere':
                if (!this.canShowPreisColumns || !this.shouldShowGewichtPreisMetricsForRow(row)) {
                    return null;
                }
                return { label: 'Einkaufspreis', value: this.getExpandedEinkaufspreis(row) };
            case 'aktuelle_gruppe':
                if (!this.canShowPreisColumns || !this.shouldShowGewichtPreisMetricsForRow(row)) {
                    return null;
                }
                return { label: 'Verkaufspreis', value: this.getExpandedVerkaufspreis(row) };
            case 'kommentar':
                if (!canShowAusstallenErloesTageszunahmen(row) || !this.canShowPreisColumns) {
                    return null;
                }
                return { label: 'Erlösdifferenz pro Tag', value: this.getExpandedErloesdifferenz(row) };
            case 'herkunft':
                if (!canShowAusstallenErloesTageszunahmen(row) || !this.canShowGewichtColumns) {
                    return null;
                }
                return { label: 'Tageszunahme', value: this.getExpandedTageszunahmen(row) };
            default:
                return null;
        }
    }

    hasPreisWert(value: LetzteAktionWertAnzahl | null | undefined): boolean {
        return value?.wert != null && !Number.isNaN(Number(value.wert));
    }

    get canOpenEinstallenShortcut(): boolean {
        return (
            this.localStorageService.hasPermission(PERMISSION_EINSTALLEN_PRIMARY) ||
            this.localStorageService.hasPermission(PERMISSION_EINSTALLEN_SECONDARY) ||
            this.localStorageService.canUseSammelstelle()
        );
    }

    showTierlisteMenu(row: TierLetzteAktionRow): boolean {
        if (row.tierliste_id == null || row.tierliste_id <= 0) {
            return false;
        }
        if (isDirectScanEinstallTierliste(row)) {
            if (canContinueDirectScanEinstall(row)) {
                return this.canOpenEinstallenShortcut;
            }
            return true;
        }
        if (row.aktion === this.einstallenAktion) {
            return true;
        }
        return row.aktion === this.ausstallenAktion;
    }

    continueDirectScan(row: TierLetzteAktionRow, event?: Event): void {
        event?.stopPropagation();
        this.openDirectScanEinstallStepper(row, 'einstallen');
    }

    listeAbschliessenDirectScan(row: TierLetzteAktionRow, event?: Event): void {
        event?.stopPropagation();
        this.openDirectScanEinstallStepper(row, 'review');
    }

    private openDirectScanEinstallStepper(
        row: TierLetzteAktionRow,
        initialStep: 'einstallen' | 'review',
    ): void {
        const gruppeId = row.gruppe_id ?? row.gruppen?.[0]?.gruppe_id;
        const einstallTierlisteId = Number(row.einstall_tierliste_id || row.tierliste_id) || 0;
        if (!gruppeId || einstallTierlisteId <= 0) {
            return;
        }
        const stepperData: DashboardEinstallenStepperDialogData = {
            continueDirectScan: {
                gruppeId,
                einstallTierlisteId,
                initialStep,
            },
        };
        this.dialog
            .open(DashboardEinstallenStepperDialogComponent, {
                ...dashboardEinstallenStepperDialogLayout,
                data: stepperData,
            })
            .afterClosed()
            .subscribe({
                next: saved => {
                    if (saved) {
                        this.resetPaginationAndLoad();
                    }
                },
            });
    }

    showRowImportIndicator(row: TierLetzteAktionRow): boolean {
        return isPendingImportLetzteAktionRow(row);
    }

    getTierlisteMenuMode(row: TierLetzteAktionRow): LetzteAktionenTierlisteMenuMode {
        if (canContinueDirectScanEinstall(row)) {
            return 'scans_fortsetzen';
        }
        if (isLetzteAktionImportPrioritaet(row)) {
            return 'importieren';
        }
        const statusId = row.status_id_anzeige ?? row.listenstatus_id;
        if (row.aktion === this.einstallenAktion) {
            if (row.tierliste_id == null || row.tierliste_id <= 0) {
                return 'abgeschlossen';
            }
            if (!isTierlisteClosed(statusId)) {
                return 'importieren';
            }
            return 'abgeschlossen';
        }
        if (row.aktion === this.ausstallenAktion && !isTierlisteClosed(statusId)) {
            return 'ausstallen';
        }
        return 'abgeschlossen';
    }

    getGruppeDetailLink(gruppeId: number): (string | number)[] {
        return ['/gruppen/aktuelle', gruppeId];
    }

    openEinstallenShortcut(): void {
        if (!this.canOpenEinstallenShortcut) {
            return;
        }
        this.dialog
            .open(DashboardEinstallenStepperDialogComponent, {
                ...dashboardEinstallenStepperDialogLayout,
            })
            .afterClosed()
            .subscribe({
                next: saved => {
                    if (saved) {
                        this.resetPaginationAndLoad();
                    }
                },
        });
    }

    /** Material icon for Herkunft/Zielbetrieb column; null when no semantic applies. */
    getHerkunftZielbetriebIcon(row: TierLetzteAktionRow): string | null {
        if (row.tierliste_id == null || row.tierliste_id <= 0) {
            return null;
        }
        switch (row.aktion) {
            case TierLetzteAktionTyp.einstallen:
                return 'login';
            case TierLetzteAktionTyp.ausstallen:
                return 'logout';
            default:
                return null;
        }
    }

    getHerkunftZielbetriebTooltip(row: TierLetzteAktionRow): string | null {
        if (row.tierliste_id == null || row.tierliste_id <= 0) {
            return null;
        }
        switch (row.aktion) {
            case TierLetzteAktionTyp.einstallen:
                return 'Herkunft';
            case TierLetzteAktionTyp.ausstallen:
                return 'Zielbetrieb';
            default:
                return null;
        }
    }

    showHerkunftZielbetriebIcon(row: TierLetzteAktionRow): boolean {
        return this.getHerkunftZielbetriebIcon(row) != null;
    }

    getHerkunftZielbetriebIconClass(row: TierLetzteAktionRow): string | null {
        switch (row.aktion) {
            case TierLetzteAktionTyp.einstallen:
                return 'herkunft-ziel-icon--herkunft';
            case TierLetzteAktionTyp.ausstallen:
                return 'herkunft-ziel-icon--zielbetrieb';
            default:
                return null;
        }
    }

    onTierlisteMenuChanged(): void {
        this.tierlisteMenuCache.invalidate();
        this.resetPaginationAndLoad();
    }

    onPageChange(event: PageEvent): void {
        const nextPageSize =
            typeof event.pageSize === 'number' && event.pageSize > 0
                ? event.pageSize
                : this.defaultPageSize;
        const pageSizeChanged = nextPageSize !== this.pageSize;
        this.pageSize = nextPageSize;
        this.pageIndex = pageSizeChanged ? 0 : event.pageIndex;
        this.loadCurrentPage();
    }

    private resetPaginationAndLoad(): void {
        this.pageIndex = 0;
        this.pageSize = this.defaultPageSize;
        this.loadCurrentPage();
    }

    refreshActions(): void {
        this.loadCurrentPage();
        this.refreshRequested.emit();
    }

    onZeitraumChange(zeitraum: LetzteAktionenZeitraum): void {
        if (this.activeZeitraum === zeitraum) {
            return;
        }
        this.activeZeitraum = zeitraum;
        saveLetzteAktionenZeitraum(zeitraum);
        this.persistZeitraumToSpeicher(zeitraum);
        this.resetPaginationAndLoad();
    }

    /**
     * Cross-device Zeitraum: speicher blob shared with mobile Aktionen filter sheet.
     * LocalStorage remains an optimistic cache for first paint.
     */
    private hydrateZeitraumFromSpeicher(): void {
        this.zeitraumSpeicherSubscription?.unsubscribe();
        this.zeitraumSpeicherSubscription = this.api.getSpeicher(LETZTE_AKTIONEN_FILTER_SPEICHER_KEY).subscribe({
            next: (raw: unknown) => {
                const fromSpeicher = parseLetzteAktionenZeitraum(raw);
                if (fromSpeicher) {
                    if (fromSpeicher !== this.activeZeitraum) {
                        this.activeZeitraum = fromSpeicher;
                        saveLetzteAktionenZeitraum(fromSpeicher);
                        this.resetPaginationAndLoad();
                    }
                    return;
                }
                // First sync: push existing local preference into speicher.
                this.persistZeitraumToSpeicher(this.activeZeitraum, raw);
            },
            error: () => {
                // keep local cache
            },
        });
    }

    private persistZeitraumToSpeicher(zeitraum: LetzteAktionenZeitraum, existing?: unknown): void {
        const write = (blob: unknown) => {
            this.api
                .setSpeicher(LETZTE_AKTIONEN_FILTER_SPEICHER_KEY, mergeLetzteAktionenFilterSpeicher(blob, zeitraum))
                .subscribe();
        };
        if (existing !== undefined) {
            write(existing);
            return;
        }
        this.api.getSpeicher(LETZTE_AKTIONEN_FILTER_SPEICHER_KEY).subscribe({
            next: (raw: unknown) => write(raw),
            error: () => write(null),
        });
    }

    getZeitraumTooltip(): string {
        const label =
            this.zeitraumOptions.find(opt => opt.value === this.activeZeitraum)?.label ?? '1 Monat';
        return `Zeitraum: ${label}`;
    }

    private loadCurrentPage(): void {
        this.fetchActions(this.pageSize, this.pageIndex * this.pageSize);
    }

    private fetchActions(limit: number, offset: number): void {
        this.loading = true;
        this.metricsLoading = false;
        this.error = null;
        this.loadActionsSubscription?.unsubscribe();

        let params = new HttpParams().set('limit', String(limit)).set('offset', String(offset));
        params = params.set('sort_key', this.activeSortKey).set('sort_dir', this.activeSortDirection);
        if (this.currentClient?.kunde_id) {
            params = params.set('kunde_id', this.currentClient.kunde_id);
        }
        params = params.set('defer_metrics', '1');
        params = params.set('zeitraum', this.activeZeitraum);

        this.loadActionsSubscription = this.api
            .get(this.apiBaseUrl + 'gruppe/read_letzte_aktionen.php', params)
            .pipe(
                finalize(() => {
                    this.loading = false;
                    this.initHorizontalScrollbarObserver();
                    this.scheduleHorizontalScrollbarSync();
                }),
            )
            .subscribe({
            next: (response: LetzteAktionenApiResponse) => {
                const records = this.normalizeRecords(response)
                    .map(row => mapLetzteAktionApiRow(row))
                    .filter((row): row is TierLetzteAktionRow => row !== null);
                const connectedRecords = this.connectCompletedUmstallPairs(records);
                const groupedRecords = this.groupConnectedTierlisteRows(connectedRecords);
                this.totalCount = Number(response?.count ?? response?.recordcount ?? groupedRecords.length) || 0;
                this.actions = groupedRecords;
                this.expandInitialRows(groupedRecords);
                this.prefetchMenuTierlisten(groupedRecords);
                this.loadDeferredMetrics(groupedRecords);
                if (this.pendingAutoScrollTierlisteId) {
                    this.scheduleScrollToHighlightedTierliste();
                }
                this.initHorizontalScrollbarObserver();
                this.scheduleHorizontalScrollbarSync();
            },
            error: () => {
                this.error = 'Letzte Aktionen konnten nicht geladen werden.';
                this.metricsLoading = false;
                if (this.actions.length === 0) {
                    this.showHorizontalScrollbar = false;
                    this.horizontalScrollWidth = 0;
                    this.horizontalScrollResizeObserver?.disconnect();
                }
            },
        });
    }

    private loadDeferredMetrics(rows: TierLetzteAktionRow[]): void {
        const canLoadMetrics = this.canShowPreisColumns || this.canShowGewichtColumns;
        if (!canLoadMetrics || rows.length === 0) {
            this.metricsLoading = false;
            return;
        }

        const payloadRows = rows.map(row => ({
            tierliste_id: row.tierliste_id ?? 0,
            gruppe_id: row.gruppe_id ?? 0,
            kunde_id: row.kunde_id ?? 0,
            aktion: row.aktion,
            datum: row.datum_raw ?? (row.datum ? row.datum.toISOString().slice(0, 19).replace('T', ' ') : ''),
        }));

        this.metricsLoading = true;
        this.api
            .post(this.apiBaseUrl + 'gruppe/read_letzte_aktionen_metrics.php', { rows: payloadRows })
            .pipe(
                finalize(() => {
                    this.metricsLoading = false;
                }),
            )
            .subscribe({
                next: (response: any) => {
                    const metricRows = Array.isArray(response?.records) ? response.records : [];
                    if (metricRows.length === 0) {
                        return;
                    }

                    const limit = Math.min(this.actions.length, metricRows.length);
                    for (let i = 0; i < limit; i++) {
                        const m = metricRows[i] ?? {};
                        const row = this.actions[i];
                        if (!shouldShowGewichtPreisMetricsForLetzteAktionRow(row)) {
                            continue;
                        }
                        if (m.darf_bio != null && m.darf_bio !== '') {
                            row.darf_bio = Number(m.darf_bio) > 0 ? 1 : 0;
                        }
                        if (!this.rowDarfBio(row)) {
                            row.einkaufspreis_avg = null;
                            row.verkaufspreis_avg = null;
                            row.erloesdifferenz_avg = null;
                            row.tageszunahmen_avg = null;
                            row.einstallgewicht_avg = null;
                            row.abgangsgewicht_avg = null;
                            continue;
                        }
                        row.einkaufspreis_avg = parseWertAnzahl(m.einkaufspreis_avg);
                        row.verkaufspreis_avg = parseWertAnzahl(m.verkaufspreis_avg);
                        row.erloesdifferenz_avg = parseWertAnzahl(m.erloesdifferenz_avg);
                        row.tageszunahmen_avg = parseWertAnzahl(m.tageszunahmen_avg);
                        row.einstallgewicht_avg = parseWertAnzahl(m.einstallgewicht_avg);
                        row.abgangsgewicht_avg = parseWertAnzahl(m.abgangsgewicht_avg);
                        const einstallTid = Number(m.einstall_tierliste_id);
                        if (einstallTid > 0) {
                            row.einstall_tierliste_id = einstallTid;
                        }
                    }
                },
                error: () => {
                    // Keep base rows visible even if deferred metrics fail.
                },
            });
    }

    private syncHorizontalScrollbar(): void {
        const tableEl = this.actionsTableViewport?.nativeElement;
        const bottomEl = this.bottomScrollbar?.nativeElement;
        if (!tableEl || !bottomEl) {
            this.showHorizontalScrollbar = false;
            return;
        }

        const tableScrollWidth = tableEl.scrollWidth;
        const tableClientWidth = tableEl.clientWidth;
        const bottomClientWidth = bottomEl.clientWidth;
        const overflowWidth = tableScrollWidth - tableClientWidth;
        this.showHorizontalScrollbar = overflowWidth > 0.5;

        // Keep bottom scrollbar range identical to the table range:
        // (contentWidth - bottomClientWidth) === (tableScrollWidth - tableClientWidth)
        this.horizontalScrollWidth = Math.max(
            tableScrollWidth + (bottomClientWidth - tableClientWidth),
            bottomClientWidth
        );

        bottomEl.scrollLeft = tableEl.scrollLeft;
    }

    private scheduleHorizontalScrollbarSync(): void {
        requestAnimationFrame(() => this.syncHorizontalScrollbar());
        setTimeout(() => this.syncHorizontalScrollbar(), 60);
    }

    private initHorizontalScrollbarObserver(): void {
        const tableEl = this.actionsTableViewport?.nativeElement;
        const nativeTable = this.actionsTableNative?.nativeElement;
        if (!tableEl || typeof ResizeObserver === 'undefined') {
            return;
        }
        this.horizontalScrollResizeObserver?.disconnect();
        this.horizontalScrollResizeObserver = new ResizeObserver(() => {
            this.syncHorizontalScrollbar();
        });
        this.horizontalScrollResizeObserver.observe(tableEl);
        if (nativeTable) {
            this.horizontalScrollResizeObserver.observe(nativeTable);
        }
    }

    private mountBottomScrollbarToBody(): void {
        const bottomEl = this.bottomScrollbar?.nativeElement;
        if (!bottomEl || this.bottomScrollbarMountedToBody) {
            return;
        }
        this.renderer.appendChild(this.document.body, bottomEl);
        this.bottomScrollbarMountedToBody = true;
    }

    private unmountBottomScrollbarFromBody(): void {
        const bottomEl = this.bottomScrollbar?.nativeElement;
        if (!bottomEl || !this.bottomScrollbarMountedToBody) {
            return;
        }
        try {
            this.renderer.removeChild(this.document.body, bottomEl);
        } catch {
            // already detached
        }
        this.bottomScrollbarMountedToBody = false;
    }

    private normalizeRecords(response: LetzteAktionenApiResponse): LetzteAktionenApiResponse['records'] {
        if (Array.isArray(response?.records)) {
            return response.records;
        }
        return [];
    }

    private prefetchMenuTierlisten(rows: TierLetzteAktionRow[]): void {
        const ids: number[] = [];
        for (const row of rows) {
            if (!this.showTierlisteMenu(row) || !row.tierliste_id) {
                continue;
            }
            ids.push(row.tierliste_id);
        }
        this.tierlisteMenuCache.prefetch(ids);
    }

    /**
     * Keep internal transfer rows together:
     * Einstallen (erstellt or abgeschlossen) -> Ausstallen (abgeschlossen).
     */
    private connectCompletedUmstallPairs(rows: TierLetzteAktionRow[]): TierLetzteAktionRow[] {
        // Keep global time sorting intact.
        // Only normalize adjacent internal-transfer pairs to:
        // Einstallen -> Ausstallen (abgeschlossen).
        const ordered = [...rows];
        for (let i = 0; i < ordered.length - 1; i++) {
            const first = ordered[i];
            const second = ordered[i + 1];
            const isAusstallThenEinstall =
                this.isAusstallenUmstallAbgeschlossen(first) && this.isEinstallenUmstallRow(second);
            if (!isAusstallThenEinstall) {
                continue;
            }
            if (!this.isConnectedUmstallPair(second, first)) {
                continue;
            }
            ordered[i] = second;
            ordered[i + 1] = first;
            i++;
        }
        return ordered;
    }

    private isEinstallenUmstallRow(row: TierLetzteAktionRow): boolean {
        if (row.aktion !== this.einstallenAktion) {
            return false;
        }
        const phase = getLetzteAktionenStatusPhase(row);
        return phase === 'erstellt' || phase === 'abgeschlossen';
    }

    private isAusstallenUmstallAbgeschlossen(row: TierLetzteAktionRow): boolean {
        if (row.aktion !== this.ausstallenAktion || row.transfer_innerhalb_betrieb !== true) {
            return false;
        }
        if (getLetzteAktionenStatusPhase(row) !== 'abgeschlossen') {
            return false;
        }
        const statusId = Number(row.status_id_anzeige ?? row.listenstatus_id ?? 0);
        return statusId >= 162;
    }

    isUmstallPairStart(index: number): boolean {
        if (index < 0 || index >= this.actions.length - 1) {
            return false;
        }
        return this.isConnectedUmstallPair(this.actions[index], this.actions[index + 1]);
    }

    isUmstallPairEnd(index: number): boolean {
        if (index <= 0 || index >= this.actions.length) {
            return false;
        }
        return this.isConnectedUmstallPair(this.actions[index - 1], this.actions[index]);
    }

    isTierlisteGroupPairStart(index: number): boolean {
        if (index < 0 || index >= this.actions.length - 1) {
            return false;
        }
        const hasPrev = index > 0 && this.isConnectedTierlisteGroupPair(this.actions[index - 1], this.actions[index]);
        const hasNext = this.isConnectedTierlisteGroupPair(this.actions[index], this.actions[index + 1]);
        return !hasPrev && hasNext;
    }

    isTierlisteGroupPairMiddle(index: number): boolean {
        if (index <= 0 || index >= this.actions.length - 1) {
            return false;
        }
        const hasPrev = this.isConnectedTierlisteGroupPair(this.actions[index - 1], this.actions[index]);
        const hasNext = this.isConnectedTierlisteGroupPair(this.actions[index], this.actions[index + 1]);
        return hasPrev && hasNext;
    }

    isTierlisteGroupPairEnd(index: number): boolean {
        if (index <= 0 || index >= this.actions.length) {
            return false;
        }
        const hasPrev = this.isConnectedTierlisteGroupPair(this.actions[index - 1], this.actions[index]);
        const hasNext =
            index < this.actions.length - 1 &&
            this.isConnectedTierlisteGroupPair(this.actions[index], this.actions[index + 1]);
        return hasPrev && !hasNext;
    }

    /** Row participates in both Umstall (green) and Tierliste-group (purple) connectors. */
    isDualConnectionHub(index: number): boolean {
        const hasUmstall = this.isUmstallPairStart(index) || this.isUmstallPairEnd(index);
        if (!hasUmstall) {
            return false;
        }
        return (
            this.isTierlisteGroupPairStart(index) ||
            this.isTierlisteGroupPairMiddle(index) ||
            this.isTierlisteGroupPairEnd(index)
        );
    }

    showUmstallDetailBridge(row: TierLetzteAktionRow, index: number): boolean {
        return this.isRowExpanded(row) && this.isUmstallPairStart(index);
    }

    showTierlisteGroupDetailBridge(row: TierLetzteAktionRow, index: number): boolean {
        if (!this.isRowExpanded(row)) {
            return false;
        }
        if (index < 0 || index >= this.actions.length - 1) {
            return false;
        }
        if (this.isDualConnectionHub(index)) {
            return false;
        }
        return this.isConnectedTierlisteGroupPair(this.actions[index], this.actions[index + 1]);
    }

    showDualConnectionDetailBridge(row: TierLetzteAktionRow, index: number): boolean {
        if (!this.isRowExpanded(row) || !this.isDualConnectionHub(index)) {
            return false;
        }
        if (index < 0 || index >= this.actions.length - 1) {
            return false;
        }
        return this.isConnectedTierlisteGroupPair(this.actions[index], this.actions[index + 1]);
    }

    private isConnectedUmstallPair(first: TierLetzteAktionRow, second: TierLetzteAktionRow): boolean {
        if (
            first.aktion !== this.einstallenAktion ||
            second.aktion !== this.ausstallenAktion ||
            second.transfer_innerhalb_betrieb !== true
        ) {
            return false;
        }
        if (!this.isAusstallenUmstallAbgeschlossen(second)) {
            return false;
        }
        if (!this.isEinstallenUmstallRow(first)) {
            return false;
        }
        return this.matchesUmstallTierlisteLink(first, second);
    }

    private matchesUmstallTierlisteLink(first: TierLetzteAktionRow, second: TierLetzteAktionRow): boolean {
        const firstIds = this.collectUmstallTierlisteIds(first);
        const secondIds = this.collectUmstallTierlisteIds(second);
        for (const id of firstIds) {
            if (secondIds.has(id)) {
                return true;
            }
        }
        const firstCount = Number(first.anzahl_tiere || 0);
        const secondCount = Number(second.anzahl_tiere || 0);
        return firstCount > 0 && firstCount === secondCount;
    }

    private collectUmstallTierlisteIds(row: TierLetzteAktionRow): Set<number> {
        const ids = new Set<number>();
        const tierlisteId = Number(row.tierliste_id || 0);
        const einstallTierlisteId = Number(row.einstall_tierliste_id || 0);
        if (tierlisteId > 0) {
            ids.add(tierlisteId);
        }
        if (einstallTierlisteId > 0) {
            ids.add(einstallTierlisteId);
        }
        return ids;
    }

    private isConnectedTierlisteGroupPair(first: TierLetzteAktionRow, second: TierLetzteAktionRow): boolean {
        const firstTierliste = Number(first.tierliste_id || 0);
        const secondTierliste = Number(second.tierliste_id || 0);
        if (firstTierliste <= 0 || secondTierliste <= 0 || firstTierliste !== secondTierliste) {
            return false;
        }
        if (first.aktion !== second.aktion) {
            return false;
        }
        const firstGroup = Number(first.gruppe_id || 0);
        const secondGroup = Number(second.gruppe_id || 0);
        if (firstGroup <= 0 || secondGroup <= 0 || firstGroup === secondGroup) {
            return false;
        }
        return true;
    }

    private groupConnectedTierlisteRows(rows: TierLetzteAktionRow[]): TierLetzteAktionRow[] {
        const eligibleKeys = new Set<string>();
        const rowsByKey = new Map<string, TierLetzteAktionRow[]>();
        const groupIdsByKey = new Map<string, Set<number>>();

        for (const row of rows) {
            const key = this.getTierlisteConnectionKey(row);
            if (!key) {
                continue;
            }
            const rowList = rowsByKey.get(key) ?? [];
            rowList.push(row);
            rowsByKey.set(key, rowList);

            const groupSet = groupIdsByKey.get(key) ?? new Set<number>();
            groupSet.add(Number(row.gruppe_id || 0));
            groupIdsByKey.set(key, groupSet);
        }

        for (const [key, rowList] of rowsByKey.entries()) {
            const distinctGroups = groupIdsByKey.get(key)?.size ?? 0;
            if (rowList.length > 1 && distinctGroups > 1) {
                eligibleKeys.add(key);
            }
        }

        if (eligibleKeys.size === 0) {
            return rows;
        }

        const ordered: TierLetzteAktionRow[] = [];
        const consumed = new Set<TierLetzteAktionRow>();
        for (const row of rows) {
            if (consumed.has(row)) {
                continue;
            }
            const key = this.getTierlisteConnectionKey(row);
            if (!key || !eligibleKeys.has(key)) {
                ordered.push(row);
                consumed.add(row);
                continue;
            }

            const groupRows = rowsByKey.get(key) ?? [row];
            for (const groupedRow of groupRows) {
                if (consumed.has(groupedRow)) {
                    continue;
                }
                ordered.push(groupedRow);
                consumed.add(groupedRow);
            }
        }

        return ordered;
    }

    private getTierlisteConnectionKey(row: TierLetzteAktionRow): string | null {
        const tierlisteId = Number(row.tierliste_id || 0);
        const gruppeId = Number(row.gruppe_id || 0);
        if (tierlisteId <= 0 || gruppeId <= 0) {
            return null;
        }
        return `${row.aktion}|${tierlisteId}`;
    }

    private scheduleScrollToHighlightedTierliste(): void {
        if (!this.highlightedTierlisteId || !this.pendingAutoScrollTierlisteId) {
            return;
        }
        for (const delay of [0, 140, 400, 900, 1600]) {
            setTimeout(() => this.scrollToHighlightedTierlisteRow(), delay);
        }
    }

    private matchesHighlightedTierliste(row: TierLetzteAktionRow, tierlisteId: number): boolean {
        const id = Number(tierlisteId || 0);
        if (id <= 0) {
            return false;
        }
        const rowTierlisteId = Number(row.tierliste_id || 0);
        const rowEinstallTierlisteId = Number(row.einstall_tierliste_id || 0);
        if (this.highlightedTierlisteMatchIds.size > 0) {
            return (
                (rowTierlisteId > 0 && this.highlightedTierlisteMatchIds.has(rowTierlisteId)) ||
                (rowEinstallTierlisteId > 0 && this.highlightedTierlisteMatchIds.has(rowEinstallTierlisteId))
            );
        }
        return rowTierlisteId === id || rowEinstallTierlisteId === id;
    }

    private scrollToHighlightedTierlisteRow(): void {
        if (!this.highlightedTierlisteId || !this.pendingAutoScrollTierlisteId) {
            return;
        }
        const viewport = this.actionsTableViewport?.nativeElement;
        if (!viewport) {
            return;
        }
        const highlighted = viewport.querySelector('tr.highlighted-tierliste-row') as HTMLElement | null;
        if (!highlighted) {
            return;
        }
        highlighted.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.pendingAutoScrollTierlisteId = null;
    }

}
